import { useState } from 'react';
import { AdminSidebar, AdminView } from './AdminSidebar';
import { AdminDashboard } from './AdminDashboard';
import { AdminReportManagement } from './AdminReportManagement';
import { AdminUserManagement } from './AdminUserManagement';
import { AdminAppSettings } from './AdminAppSettings';
import { AdminGuide } from './AdminGuide';
import { AdminTrainingChecklist } from './AdminTrainingChecklist';
import { AdminIncidentResponse } from './AdminIncidentResponse';
import { User as FirebaseUser } from 'firebase/auth';

interface UserProfile {
  displayName: string;
  photoUrl?: string | null;
}

interface AdminPanelProps {
  currentUser: FirebaseUser;
  currentUserRole: 'root' | 'admin';
  onBackToHome: () => void;
  userProfile: UserProfile | null;
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
  confirmAction?: (title: string, message: string, action: () => void) => void;
}

export const AdminPanel = ({
  currentUser,
  currentUserRole,
  onBackToHome,
  userProfile,
  showNotify,
  confirmAction,
}: AdminPanelProps) => {
  const [currentView, setCurrentView] = useState<AdminView>('dashboard');

  // Security check: Ensure user has admin role
  if (!currentUser || !currentUserRole || (currentUserRole !== 'root' && currentUserRole !== 'admin')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-6">You do not have permission to access the admin panel.</p>
          <button
            onClick={onBackToHome}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  const userName = userProfile?.displayName || currentUser.displayName || 'Admin';
  const userPhoto = currentUser.photoURL || userProfile?.photoUrl || null;

  const handleViewChange = (view: AdminView) => {
    setCurrentView(view);
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
        return (
          <AdminDashboard
            currentUserId={currentUser.uid}
            currentUserRole={currentUserRole}
            showNotify={showNotify}
            confirmAction={confirmAction}
            // No onClose for page mode
          />
        );
      case 'reports':
        return (
          <AdminReportManagement
            currentUserId={currentUser.uid}
            currentUserRole={currentUserRole}
            // No onClose for page mode
          />
        );
      case 'users':
        if (currentUserRole === 'root') {
          return (
            <AdminUserManagement
              currentUserId={currentUser.uid}
              currentUserRole={currentUserRole}
              // No onClose for page mode
            />
          );
        }
        return null;
      case 'settings':
        return (
          <AdminAppSettings
            currentUserId={currentUser.uid}
            currentUserRole={currentUserRole}
            showNotify={showNotify}
            confirmAction={confirmAction}
            // No onClose for page mode
          />
        );
      case 'guide':
        return (
          <AdminGuide
            currentUserRole={currentUserRole}
          />
        );
      case 'training':
        return (
          <AdminTrainingChecklist
            currentUserId={currentUser.uid}
            currentUserRole={currentUserRole}
          />
        );
      case 'incidents':
        return (
          <AdminIncidentResponse
            currentUserRole={currentUserRole}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar Navigation - Fixed */}
      <AdminSidebar
        currentView={currentView}
        onViewChange={handleViewChange}
        onBackToHome={onBackToHome}
        userRole={currentUserRole}
        userName={userName}
        userPhoto={userPhoto}
      />

      {/* Main Content Area - Offset for fixed sidebar */}
      <main className="lg:ml-64 min-w-0">
        {/* Mobile Header Spacer */}
        <div className="h-16 lg:hidden" />

        {/* Content */}
        <div className="p-4 lg:p-6">
          <div className="max-w-7xl mx-auto">
            {renderCurrentView()}
          </div>
        </div>
      </main>
    </div>
  );
};
